import { Button, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { type ComponentType, type CSSProperties, type ReactNode } from 'react';
import ClosePopupIcon from '../../images/ClosePopupIcon.svg';

export interface IConfirmPopupContentProps {
  /** Primary (right) button handler. */
  rightButtonAction?: () => void;
  /** Secondary (left) button handler. */
  leftButtonAction?: () => void;
  /** Primary button label. Omit to hide the button. */
  rightButtonText?: ReactNode;
  /** Secondary button label. Omit to hide the button. */
  leftButtonText?: ReactNode;
  /** Heading content — plain text or arbitrary nodes. */
  title?: ReactNode;
  /** Style override for the heading. */
  titleStyle?: CSSProperties;
  /**
   * Body copy. A render component (function) is invoked as `<Description />`;
   * any other node (string or element) is rendered inside the styled paragraph.
   */
  description?: ReactNode | ComponentType;
  /** Style override for the body copy wrapper. */
  descriptionStyle?: CSSProperties;
  /** Optional icon component rendered above the title. */
  Icon?: ComponentType;
  /** Dismiss handler, wired to the close (X) icon. */
  closePopup?: () => void;
  /** Shows the close (X) icon in the top-right corner. */
  showClosePopupIcon?: boolean;
  /** Arbitrary body content rendered between the description and the buttons. */
  children?: ReactNode;
  leftButtonStyle?: CSSProperties;
  rightButtonStyle?: CSSProperties;
  rightButtonDisabled?: boolean;
  leftButtonDisabled?: boolean;
  rightButtonLoading?: boolean;
  closePopupDataCy?: string;
  leftButtonDataCy?: string;
  rightButtonDataCy?: string;
}

/**
 * ConfirmPopupContent
 *
 * The inner layout of {@link ConfirmPopup}: optional close icon, optional icon,
 * title, description (string or render component), arbitrary children, and up to
 * two action buttons. Kept separate from the modal chrome so it can be unit
 * tested without a portal/overlay.
 */
export const ConfirmPopupContent = ({
  rightButtonAction,
  leftButtonAction,
  rightButtonText,
  leftButtonText,
  title,
  titleStyle,
  description,
  descriptionStyle,
  Icon,
  closePopup,
  showClosePopupIcon,
  children,
  leftButtonStyle,
  rightButtonStyle,
  rightButtonDisabled,
  leftButtonDisabled,
  rightButtonLoading,
  closePopupDataCy,
  leftButtonDataCy,
  rightButtonDataCy,
}: IConfirmPopupContentProps) => {
  const renderDescription = (): ReactNode => {
    if (!description) {
      return null;
    }

    if (typeof description === 'function') {
      const DescriptionComponent = description;
      return (
        <div
          style={{
            marginBottom: 70,
            ...descriptionStyle,
          }}
        >
          <DescriptionComponent />
        </div>
      );
    }

    return (
      <Typography
        as="p"
        textAlign="center"
        color={colors.neutral[500]}
        style={{
          width: '80%',
          fontSize: 16,
          margin: 0,
          marginBottom: 70,
          ...descriptionStyle,
        }}
      >
        {description}
      </Typography>
    );
  };

  return (
    <>
      {showClosePopupIcon && (
        <img
          alt="close-popup-icon"
          src={ClosePopupIcon}
          data-cy={closePopupDataCy}
          data-testid="close-popup"
          style={{
            position: 'absolute',
            marginTop: -24,
            marginRight: 16,
            cursor: 'pointer',
            alignSelf: 'flex-end',
          }}
          onClick={closePopup}
        />
      )}

      {Icon && <Icon />}

      {title && (
        <Typography
          as="h1"
          bold
          textAlign="center"
          color={colors.neutral[600]}
          style={{
            fontSize: 18,
            margin: 0,
            marginBottom: 20,
            ...titleStyle,
          }}
        >
          {title}
        </Typography>
      )}

      {renderDescription()}

      {children}

      <Row gap={4}>
        {leftButtonText && (
          <Button
            variant="secondary"
            data-cy={leftButtonDataCy}
            style={leftButtonStyle}
            onClick={leftButtonAction}
            disabled={leftButtonDisabled}
          >
            {leftButtonText}
          </Button>
        )}

        {rightButtonText && (
          <Button
            data-cy={rightButtonDataCy}
            data-testid="right-button"
            type="button"
            style={rightButtonStyle}
            onClick={rightButtonAction}
            disabled={rightButtonDisabled}
            isLoading={rightButtonLoading}
          >
            {rightButtonText}
          </Button>
        )}
      </Row>
    </>
  );
};
